/**
 * MongoDB 用户个人主页
 * 零侵入设计，与现有 Profile 页面完全独立
 * 路由: /profile-v2
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Drawer,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { userApi, formatDate, getMembershipInfo, getGrowthLevel } from '../services/mongoUserApi';
import { getWatchOverview, getCurrentUserId, getGuestUserId } from '../services/watchApi';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import MobileMenuHandle from '../components/profile/MobileMenuHandle';
import BasicInfoTab from '../components/profile/BasicInfoTab';
import MembershipTab from '../components/profile/MembershipTab';
import WalletTab from '../components/profile/WalletTab';
import WatchStatsTab from '../components/profile/WatchStatsTab';
import WatchHistoryTab from '../components/profile/WatchHistoryTab';
import FavoritesTab from '../components/profile/FavoritesTab';

const MongoProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const { mongoUser, isLoggedInMongo, checkMongoHealth, updateUserState } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [watchStats, setWatchStats] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 获取用户完整信息和观看统计
  const fetchUserInfo = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      
      // 并行获取用户信息和观看统计
      const [userResult, statsResult] = await Promise.all([
        userApi.getById(userId),
        getWatchOverview(userId)
      ]);
      
      if (userResult.success && userResult.data) {
        setUser(userResult.data);
        // 同时更新全局状态
        updateUserState(userResult.data);
      } else {
        setError(userResult.message || '获取用户信息失败');
      }
      
      if (statsResult.success && statsResult.data) {
        setWatchStats(statsResult.data);
      }
    } catch (err) {
      console.error('获取用户信息失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 刷新用户信息
  const refreshUserInfo = async () => {
    if (mongoUser && mongoUser.user_id) {
      await fetchUserInfo(mongoUser.user_id);
    }
  };

  // 检查登录状态并加载用户信息
  useEffect(() => {
    console.log('MongoProfile 状态检查:', { 
      isLoggedInMongo, 
      hasMongoUser: !!mongoUser,
      mongoUserId: mongoUser?.user_id,
      hasLocalStorageUser: !!localStorage.getItem('mongo_user')
    });

    // 给状态同步一点时间，避免刚登录时的竞态条件
    const timer = setTimeout(() => {
      // 双重检查：同时检查 React state 和 localStorage
      const hasLocalUser = localStorage.getItem('mongo_user');

      if (!isLoggedInMongo && !mongoUser && !hasLocalUser) {
        console.log('未检测到登录状态，跳转到登录页');
        history.replace('/login-v2');
        return;
      }

      // 获取用户 ID - 尝试多种可能的字段名
      let userId = null;
      if (mongoUser) {
        userId = mongoUser.user_id || mongoUser._id || mongoUser.id || mongoUser.userId;
      }
      
      // 如果 React state 中没有，尝试从 localStorage 读取
      if (!userId && hasLocalUser) {
        try {
          const parsed = JSON.parse(hasLocalUser);
          userId = parsed.user_id || parsed._id || parsed.id || parsed.userId;
          console.log('从 localStorage 解析的用户 ID:', userId, '原始数据:', parsed);
        } catch (e) {
          console.error('解析 localStorage 用户数据失败:', e);
        }
      }

      if (!userId) {
        console.log('无法获取用户 ID, mongoUser:', mongoUser, 'hasLocalUser:', hasLocalUser);
        history.replace('/login-v2');
        return;
      }

      console.log('开始加载用户信息, user_id:', userId);
      // 只加载一次：如果 user 状态已经有数据且 ID 相同，不重复加载
      if (!user || user.user_id !== userId) {
        checkMongoHealth().then(() => {
          fetchUserInfo(userId);
        });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // 处理 Tab 切换
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // 渲染当前 Tab 内容
  const renderTabContent = () => {
    if (!user) return null;

    switch (activeTab) {
      case 'basic':
        return <BasicInfoTab user={user} onUpdate={refreshUserInfo} />;
      case 'membership':
        return <MembershipTab user={user} />;
      case 'wallet':
        return <WalletTab user={user} />;
      case 'stats':
        return <WatchStatsTab user={user} />;
      case 'favorites':
        return <FavoritesTab user={user} />;
      case 'history':
        return <WatchHistoryTab user={user} />;
      case 'settings':
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="info">账号设置功能开发中...</Alert>
          </Box>
        );
      default:
        return <BasicInfoTab user={user} onUpdate={refreshUserInfo} />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">用户不存在</Alert>
      </Container>
    );
  }

  const membershipInfo = getMembershipInfo(user.membership?.level);
  const growthInfo = getGrowthLevel(user.points?.growth_value || 0);

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: isMobile ? 1 : 2,
        mb: 4,
        px: isMobile ? 1 : 2,
      }}
    >
      {/* 顶部用户信息卡片 */}
      <ProfileHeader
        user={user}
        membershipInfo={membershipInfo}
        growthInfo={growthInfo}
        formatDate={formatDate}
        watchStats={watchStats}
      />

      {/* 移动端：左边缘悬浮"拉手"菜单触发器（方案 K）
          - 永远贴在屏幕左侧，周期性招手动画引导用户发现
          - 抽屉打开时图标变 ✕，方便关闭
          - 桌面端不渲染 */}
      {isMobile && (
        <MobileMenuHandle
          open={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((prev) => !prev)}
        />
      )}

      {/* 主内容区域 */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          flexDirection: isMobile ? 'column' : 'row',
          mt: isMobile ? 2 : 3, // 与顶部 ProfileHeader 拉开间距，避免卡片紧贴
        }}
      >
        {/* 侧边栏 - 桌面端 */}
        {!isMobile && (
          <Box sx={{ width: 240, flexShrink: 0 }}>
            <ProfileSidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              user={user}
            />
          </Box>
        )}

        {/* 侧边栏 - 移动端抽屉 */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        >
          <Box sx={{ width: 280, pt: 2 }}>
            <ProfileSidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              user={user}
            />
          </Box>
        </Drawer>

        {/* 右侧内容区域 */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {renderTabContent()}
        </Box>
      </Box>
    </Container>
  );
};

export default MongoProfile;
